import React, { type ComponentType, useEffect, useState } from 'react';

import _orderBy from 'lodash-es/orderBy';

import type { IconName } from '@iconik-io/uad';

import Button from '../../../ui/button/button';
import SortableList from '../../sortable_list/sortable_list';
import KeyValueItem, {
  type KeyValueItemProps,
  type StateValue,
} from './key_value_item';

import styles from './key_value_item.module.css';

const iconNames: { desc: IconName; asc: IconName } = {
  desc: 'arrow_small_s',
  asc: 'arrow_small_n',
};

export type MultiKeyValueType = { label: string; value: string };

export type MultiKeyValueProps = {
  value?: MultiKeyValueType[];
  name?: string;
  onChange?: (value: MultiKeyValueType[]) => void;
  className?: string;
  labelPlaceholder?: string;
  valuePlaceholder?: string;
  autofillValue?: boolean;
  disabled?: boolean;
  isSortable?: boolean;
  keyItemComponent?: ComponentType<KeyValueItemProps>;
};

const MultiKeyValue = (props: MultiKeyValueProps) => {
  const [sort, setSort] = useState<'asc' | 'desc' | undefined>(undefined);
  const [showAll, setShowAll] = useState(false);
  const [values, setValues] = useState<StateValue[]>([]);
  const [keyCounter, setKeyCounter] = useState<string | undefined>();

  useEffect(() => {
    if (props.value) {
      const items = props.value.map((valueItem, id) => {
        const { label, value } = valueItem;
        return { id: String(id), label, value };
      });
      setValues(items);
    } else {
      setValues([]);
    }
    setKeyCounter(String(values.length));
  }, [props.value, values.length]);

  const listRef = React.createRef<HTMLDivElement>();

  const endRef = React.createRef<HTMLHRElement>();

  const getNewKey = () => {
    const updatedKeyCounter = keyCounter
      ? Number(keyCounter) + 1
      : values.length + 1;
    setKeyCounter(String(updatedKeyCounter));
    return String(updatedKeyCounter);
  };

  const onChange = (items: StateValue[]): void => {
    props.onChange?.(items.map(({ label, value }) => ({ label, value })));
  };

  const handleChange = (
    changedId: string,
    label: string,
    value: string,
  ): void => {
    const updatedValues = [...values];
    const changeIndex = values.findIndex(({ id }) => changedId === id);
    if (changeIndex >= 0) {
      updatedValues[changeIndex] = { id: changedId, label, value };
    } else {
      updatedValues.push({ id: changedId, label, value });
    }
    onChange(updatedValues);
  };

  const handleDelete = (deleteId: string): void => {
    const updatedValues = [...values];
    const deleteIndex = values.findIndex(({ id }) => deleteId === id);
    updatedValues.splice(deleteIndex, 1);

    setValues(updatedValues);
    onChange(updatedValues);
  };

  const newOption = (): void => {
    const updatedValues: StateValue[] = [
      ...values,
      { id: getNewKey(), label: '', value: '' },
    ];
    setSort(undefined);
    setValues(updatedValues);
    onChange(updatedValues);
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      newOption();
    }
  };

  const sortFields = (): void => {
    const sortType = sort === 'desc' ? 'asc' : 'desc';

    const updatedValues = _orderBy(
      values,
      [(item: StateValue) => item.label.toLowerCase()],
      sort,
    );
    setSort(sortType);
    setValues(updatedValues);
    onChange(updatedValues);
  };

  const showAllFields = (): void => {
    setShowAll(!showAll);
  };

  const onDropElement = (items: StateValue[]): void => {
    setSort(undefined);
    setValues(items);
    onChange(items);
  };

  const { className, isSortable = true } = props;
  const sortingIconName = sort ? iconNames[sort] : 'sort';

  const ItemComponent = props.keyItemComponent ?? KeyValueItem;

  return (
    <div className={className}>
      <div
        ref={listRef}
        className={styles.wrapper}
        style={{ maxHeight: showAll ? undefined : 230 }}
      >
        <SortableList
          items={values}
          itemKeyProperty="id"
          onItemsUpdated={onDropElement}
          itemWrapperClassName={styles.item_wrapper}
          itemComponent={ItemComponent}
          itemComponentProps={{
            autofillValue: props.autofillValue ?? true,
            fieldName: props.name ?? 'multi_key_value',
            labelPlaceholder: props.labelPlaceholder ?? 'Label',
            movable: values.length > 1,
            onChange: handleChange,
            onDeleteClick: handleDelete,
            onKeyDown: handleKeyDown,
            valuePlaceholder: props.valuePlaceholder ?? 'Value',
            disabled: props.disabled,
          }}
        />
        <hr ref={endRef} className={styles.hr} />
      </div>
      <Button
        iconName="plus"
        onClick={() => newOption()}
        size="s"
        className={styles.action}
        disabled={props.disabled}
      >
        Add
      </Button>
      {isSortable && values.length > 1 ? (
        <Button
          onClick={sortFields}
          size="s"
          iconName={sortingIconName}
          className={styles.action}
        >
          {' Abc'}
        </Button>
      ) : undefined}
      {values.length > 5 ? (
        <Button
          noBorder
          variant="info"
          onClick={showAllFields}
          size="s"
          className={styles.show_all}
        >
          {showAll ? 'Hide' : 'Show All'}
        </Button>
      ) : undefined}
    </div>
  );
};

export default MultiKeyValue;
